import { projectAwaitsStudy } from "@afar-research/afar";
import { getProjectWorkspacePreference } from "../projectPresentation";
import {
  scopedProjectKey,
  scopeProjectRef,
  scopeThreadRef,
} from "@afar-research/client-runtime/environment";
import {
  DEFAULT_RUNTIME_MODE,
  DEFAULT_SERVER_SETTINGS,
  type EnvironmentId,
  type ScopedProjectRef,
  type ThreadActionSelection,
  type ThreadId,
} from "@afar-research/contracts";
import { useParams, useRouter } from "@tanstack/react-router";
import { useCallback, useMemo } from "react";
import {
  markPromotedDraftThreadByRef,
  type DraftId,
  type DraftThreadEnvMode,
  type DraftThreadState,
  useComposerDraftStore,
} from "../composerDraftStore";
import { newDraftId, newThreadId } from "../lib/utils";
import {
  resolveSidebarNewThreadProjectMember,
  orderItemsByPreferredIds,
} from "../components/Sidebar.logic";
import {
  deriveLogicalProjectKeyFromSettings,
  getProjectOrderKey,
  selectProjectGroupingSettings,
} from "../logicalProject";
import { readThreadShell, useProjects, useServerConfigs, useThread } from "../state/entities";
import { resolveNewDraftStartFromOrigin } from "../lib/chatThreadActions";
import { buildThreadRouteParams, resolveThreadRouteTarget } from "../threadRoutes";
import { useUiStateStore } from "../uiStateStore";
import { useClientSettings } from "./useSettings";

/**
 * The key of the one reusable draft a logical project keeps per Environment:
 * opening a new thread there again returns to the unsent draft rather than
 * starting a second one.
 */
export function draftLogicalProjectKey(
  logicalProjectKey: string,
  environmentId: EnvironmentId,
): string {
  return `${environmentId}\u0000${logicalProjectKey}`;
}

export function useNewThreadHandler() {
  const projects = useProjects();
  const serverConfigs = useServerConfigs();

  const projectGroupingSettings = useClientSettings(selectProjectGroupingSettings);
  const router = useRouter();
  const getCurrentRouteTarget = useCallback(() => {
    const currentRouteParams = router.state.matches[router.state.matches.length - 1]?.params ?? {};
    return resolveThreadRouteTarget(currentRouteParams);
  }, [router]);

  return useCallback(
    (
      projectRef: ScopedProjectRef,
      options?: {
        branch?: string | null;
        worktreePath?: string | null;
        envMode?: DraftThreadEnvMode;
        startFromOrigin?: boolean;
        forceNew?: boolean;
        draftIdentity?: { draftId: DraftId; threadId: ThreadId };
        /** The Action chip the new draft starts with, such as "New Experiment thread". */
        action?: ThreadActionSelection;
        initialPrompt?: string;
        initialModelSelection?: Parameters<
          ReturnType<typeof useComposerDraftStore.getState>["setModelSelection"]
        >[1];
      },
    ): Promise<{ draftId: DraftId; threadId: ThreadId } | null> => {
      const {
        getDraftSessionByLogicalProjectKey,
        getDraftSession,
        getDraftThread,
        applyStickyState,
        setDraftThreadContext,
        setLogicalProjectDraftThreadId,
      } = useComposerDraftStore.getState();
      if (options?.draftIdentity) {
        const identity = options.draftIdentity;
        const existing = getDraftSession(identity.draftId);
        const threadRef = scopeThreadRef(projectRef.environmentId, identity.threadId);
        if (
          existing &&
          (existing.environmentId !== projectRef.environmentId ||
            existing.projectId !== projectRef.projectId ||
            existing.threadId !== identity.threadId)
        ) {
          return Promise.reject(
            new Error("The prepared draft belongs to a different thread context."),
          );
        }
        const promotedTo = existing?.promotedTo ?? (readThreadShell(threadRef) ? threadRef : null);
        if (promotedTo) {
          return router
            .navigate({
              to: "/$environmentId/$threadId",
              params: buildThreadRouteParams(promotedTo),
            })
            .then(() => null);
        }
        if (existing) {
          return router
            .navigate({ to: "/draft/$draftId", params: { draftId: identity.draftId } })
            .then(() => ({ draftId: identity.draftId, threadId: identity.threadId }));
        }
      }
      const currentRouteTarget = getCurrentRouteTarget();
      const project = projects.find(
        (candidate) =>
          candidate.id === projectRef.projectId &&
          candidate.environmentId === projectRef.environmentId,
      );
      // A project awaiting its Project Study keeps to its ingest thread; the
      // controls that lead here are disabled, and this holds the line for any
      // path that still reaches it.
      if (project !== undefined && projectAwaitsStudy(project)) {
        return Promise.resolve(null);
      }
      // Preset the chip on whichever draft this lands on.
      const presetAction = (draftId: DraftId) => {
        if (options?.action) {
          useComposerDraftStore.getState().setComposerAction(draftId, options.action);
        }
      };
      const environmentSettings =
        serverConfigs.get(projectRef.environmentId)?.settings ?? DEFAULT_SERVER_SETTINGS;
      const logicalProjectKey = project
        ? deriveLogicalProjectKeyFromSettings(project, projectGroupingSettings)
        : scopedProjectKey(projectRef);
      const reusableDraftKey = draftLogicalProjectKey(logicalProjectKey, projectRef.environmentId);
      const draftKey = options?.draftIdentity
        ? `${reusableDraftKey}\u0000draft:${options.draftIdentity.draftId}`
        : reusableDraftKey;
      const hasBranchOption = options?.branch !== undefined;
      const hasWorktreePathOption = options?.worktreePath !== undefined;
      const hasEnvModeOption = options?.envMode !== undefined;
      const hasStartFromOriginOption = options?.startFromOrigin !== undefined;
      const storedDraftThread =
        options?.forceNew || options?.draftIdentity
          ? null
          : getDraftSessionByLogicalProjectKey(draftKey);
      const storedDraftThreadRef = storedDraftThread
        ? scopeThreadRef(storedDraftThread.environmentId, storedDraftThread.threadId)
        : null;
      const reusableStoredDraftThread =
        storedDraftThreadRef && readThreadShell(storedDraftThreadRef) !== null
          ? null
          : storedDraftThread;
      if (storedDraftThreadRef && reusableStoredDraftThread === null) {
        markPromotedDraftThreadByRef(storedDraftThreadRef);
      }
      const latestActiveDraftThread: DraftThreadState | null = currentRouteTarget
        ? currentRouteTarget.kind === "server"
          ? getDraftThread(currentRouteTarget.threadRef)
          : getDraftSession(currentRouteTarget.draftId)
        : null;
      if (reusableStoredDraftThread) {
        return (async () => {
          if (
            hasBranchOption ||
            hasWorktreePathOption ||
            hasEnvModeOption ||
            hasStartFromOriginOption
          ) {
            setDraftThreadContext(reusableStoredDraftThread.draftId, {
              ...(hasBranchOption ? { branch: options?.branch ?? null } : {}),
              ...(hasWorktreePathOption ? { worktreePath: options?.worktreePath ?? null } : {}),
              ...(hasEnvModeOption ? { envMode: options?.envMode } : {}),
              ...(hasStartFromOriginOption ? { startFromOrigin: options?.startFromOrigin } : {}),
            });
          }
          setLogicalProjectDraftThreadId(draftKey, projectRef, reusableStoredDraftThread.draftId, {
            threadId: reusableStoredDraftThread.threadId,
          });
          presetAction(reusableStoredDraftThread.draftId);
          if (
            !(
              currentRouteTarget?.kind === "draft" &&
              currentRouteTarget.draftId === reusableStoredDraftThread.draftId
            )
          ) {
            await router.navigate({
              to: "/draft/$draftId",
              params: { draftId: reusableStoredDraftThread.draftId },
            });
          }
          return {
            draftId: reusableStoredDraftThread.draftId,
            threadId: reusableStoredDraftThread.threadId,
          };
        })();
      }

      if (
        !options?.forceNew &&
        !options?.draftIdentity &&
        latestActiveDraftThread &&
        currentRouteTarget?.kind === "draft" &&
        latestActiveDraftThread.logicalProjectKey === draftKey &&
        latestActiveDraftThread.promotedTo == null
      ) {
        if (
          hasBranchOption ||
          hasWorktreePathOption ||
          hasEnvModeOption ||
          hasStartFromOriginOption
        ) {
          setDraftThreadContext(currentRouteTarget.draftId, {
            ...(hasBranchOption ? { branch: options?.branch ?? null } : {}),
            ...(hasWorktreePathOption ? { worktreePath: options?.worktreePath ?? null } : {}),
            ...(hasEnvModeOption ? { envMode: options?.envMode } : {}),
            ...(hasStartFromOriginOption ? { startFromOrigin: options?.startFromOrigin } : {}),
          });
        }
        setLogicalProjectDraftThreadId(draftKey, projectRef, currentRouteTarget.draftId, {
          threadId: latestActiveDraftThread.threadId,
          createdAt: latestActiveDraftThread.createdAt,
          runtimeMode: latestActiveDraftThread.runtimeMode,
          interactionMode: latestActiveDraftThread.interactionMode,
          ...(hasBranchOption ? { branch: options?.branch ?? null } : {}),
          ...(hasWorktreePathOption ? { worktreePath: options?.worktreePath ?? null } : {}),
          ...(hasEnvModeOption ? { envMode: options?.envMode } : {}),
          ...(hasStartFromOriginOption ? { startFromOrigin: options?.startFromOrigin } : {}),
        });
        presetAction(currentRouteTarget.draftId);
        return Promise.resolve({
          draftId: currentRouteTarget.draftId,
          threadId: latestActiveDraftThread.threadId,
        });
      }

      const draftId = options?.draftIdentity?.draftId ?? newDraftId();
      const threadId = options?.draftIdentity?.threadId ?? newThreadId();
      const createdAt = new Date().toISOString();
      const initialEnvMode =
        options?.envMode ??
        getProjectWorkspacePreference(projectRef) ??
        environmentSettings.defaultThreadEnvMode;
      return (async () => {
        setLogicalProjectDraftThreadId(draftKey, projectRef, draftId, {
          threadId,
          createdAt,
          branch: options?.branch ?? null,
          worktreePath: options?.worktreePath ?? null,
          envMode: initialEnvMode,
          startFromOrigin:
            options?.startFromOrigin ??
            resolveNewDraftStartFromOrigin({
              envMode: initialEnvMode,
              newWorktreesStartFromOrigin: environmentSettings.newWorktreesStartFromOrigin,
            }),
          runtimeMode: DEFAULT_RUNTIME_MODE,
        });
        applyStickyState(draftId);
        if (options?.initialModelSelection) {
          useComposerDraftStore
            .getState()
            .setModelSelection(draftId, options.initialModelSelection);
        }
        if (options?.initialPrompt) {
          useComposerDraftStore.getState().setPrompt(draftId, options.initialPrompt);
        }
        presetAction(draftId);

        await router.navigate({
          to: "/draft/$draftId",
          params: { draftId },
        });
        return { draftId, threadId };
      })();
    },
    [getCurrentRouteTarget, projectGroupingSettings, projects, router, serverConfigs],
  );
}

export function useHandleNewThread() {
  const projectOrder = useUiStateStore((store) => store.projectOrder);
  const routeTarget = useParams({
    strict: false,
    select: (params) => resolveThreadRouteTarget(params),
  });
  const routeThreadRef = routeTarget?.kind === "server" ? routeTarget.threadRef : null;
  const routeDraftId = routeTarget?.kind === "draft" ? routeTarget.draftId : null;
  const activeThread = useThread(routeThreadRef);
  const getDraftThread = useComposerDraftStore((store) => store.getDraftThread);
  const activeDraftThread = useComposerDraftStore(() =>
    routeTarget
      ? routeTarget.kind === "server"
        ? getDraftThread(routeTarget.threadRef)
        : useComposerDraftStore.getState().getDraftSession(routeTarget.draftId)
      : null,
  );
  const projects = useProjects();
  const orderedProjects = useMemo(() => {
    return orderItemsByPreferredIds({
      items: projects,
      preferredIds: projectOrder,
      getId: getProjectOrderKey,
      getPreferenceIds: (project) => [getProjectOrderKey(project)],
    });
  }, [projectOrder, projects]);
  const settings = useClientSettings((settings) => settings);
  const serverConfigs = useServerConfigs();
  const scopedProjects = settings.sidebarProjectScopeKey
    ? orderedProjects.filter(
        (project) =>
          deriveLogicalProjectKeyFromSettings(project, selectProjectGroupingSettings(settings)) ===
          settings.sidebarProjectScopeKey,
      )
    : [];
  const blockedEnvironments = new Set(
    scopedProjects
      .filter((project) => !serverConfigs.has(project.environmentId))
      .map((project) => project.environmentId),
  );
  const preferredProject = resolveSidebarNewThreadProjectMember(
    scopedProjects,
    blockedEnvironments,
  );
  const handleNewThread = useNewThreadHandler();

  return {
    activeDraftThread,
    activeThread,
    preferredProjectRef: preferredProject
      ? scopeProjectRef(preferredProject.environmentId, preferredProject.id)
      : null,
    defaultProjectRef: orderedProjects[0]
      ? scopeProjectRef(orderedProjects[0].environmentId, orderedProjects[0].id)
      : null,
    handleNewThread,
    routeDraftId,
    routeThreadRef,
  };
}
